import { ChildProps } from '@apollo/client/react/hoc';
import { MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { gql } from '@reverbdotcom/commons/src/gql';
import React, { useState } from 'react';
import I18n from 'i18n-js';

import {
  RCModal,
  RCText,
  RCCheckbox,
  RCTextArea,
  RCTextInput,
  RCLoadingBars,
  useMediaQuery,
  RCIcon,
} from '@reverbdotcom/cadence/components';
import { HeartStraightIcon, ShoppingCartIcon } from '@reverbdotcom/cadence/icons/react';
import { formatNumber, parseAmountCents } from '@reverbdotcom/commons/src/money';
import Currency from '@reverbdotcom/commons/src/currency';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import {
  createAutoOfferMutation,
  deleteAutoOfferMutation,
} from '@reverbdotcom/commons/src/offers/offer_operations';
import {
  CoreAutoOfferModal,
  AutoOfferListingFragment as RQLAutoOfferListingFragment,
  CoreAutoOffersCreateAutoOfferMutation,
  CoreAutoOffersDeleteAutoOfferMutation,
  core_apimessages_Money as Money,
} from '@reverbdotcom/commons/src/gql/graphql';
import Location from '../../lib/wrapped_location';
import { connect } from '@reverbdotcom/commons/src/connect';
import BLANK_MEDIUM_IMAGE from '../../../images/products/blank_medium.jpg';
import { formReducer } from './direct_offer_reducer';

const MINIMUM_DISCOUNT_PERCENT = 5;
const DEFAULT_DISCOUNT_PERCENT = 10;
export const DIRECT_OFFER_HELP_CENTER_ARTICLE_ID = '10672996858387';

export const AUTO_OFFER_ACTION = {
  CREATED: 'created',
  DELETED: 'deleted',
} as const;

export type AutoOfferAction = (typeof AUTO_OFFER_ACTION)[keyof typeof AUTO_OFFER_ACTION];

interface ExternalProps {
  listingId: string;
  modalIsOpen: boolean;
  onModalClose: () => void;
  refetchSellerListing?: (action: AutoOfferAction, appliedOfferPercent?: number) => void;
}

export interface IApolloProps {
  createAutoOffer: MutationFunction<
    CoreAutoOffersCreateAutoOfferMutation.Mutation,
    CoreAutoOffersCreateAutoOfferMutation.Variables
  >;
  deleteAutoOffer: MutationFunction<
    CoreAutoOffersDeleteAutoOfferMutation.Mutation,
    CoreAutoOffersDeleteAutoOfferMutation.Variables
  >;
}

type Props = ChildProps<ExternalProps, CoreAutoOfferModal.Query> & IApolloProps;

export const emptyFormState = {
  autoOfferIsActive: false,
  messageToBuyer: '',
  submitFailedMessage: '',
  percentage: DEFAULT_DISCOUNT_PERCENT,
  shippingPriceOverride: { amount: '' },
};

export interface IState {
  messageToBuyer: string;
  autoOfferIsActive: boolean;
  submitFailedMessage: string;
  percentage: number;
  shippingPriceOverride: Money;
}

function t(key: string, opts?: object) {
  return I18n.t(`discovery.listingsManagement.directOffersModal.${key}`, opts);
}

function percentageValidationError(formState: IState) {
  if (!formState.percentage || formState.percentage < MINIMUM_DISCOUNT_PERCENT) {
    return t('percentOff.minimumError', { minimum: MINIMUM_DISCOUNT_PERCENT });
  }
  if (formState.percentage >= 100) {
    return t('percentOff.maximumError');
  }
  return null;
}

function discountedPrice(amountCents: number, currency: string, percentDiscount: number): string {
  const discountMultiplier = (100 - percentDiscount) / 100;
  const discountedPriceCents = Math.floor(amountCents * discountMultiplier);
  const parsed = parseAmountCents(discountedPriceCents, currency);

  return formatNumber(Number(parsed.amount));
}

function hasAutoOffer(listing: RQLAutoOfferListingFragment.Fragment) {
  return !!listing && !!listing.autoOffers.id;
}

function listingTaxIncluded(listing: RQLAutoOfferListingFragment.Fragment) {
  return !!listing.taxIncluded && !!listing.taxIncludedHint;
}

function userCurrencyMatchesShopCurrency(listing: RQLAutoOfferListingFragment.Fragment) {
  return listing.price.currency === listing.sellerPrice.currency;
}

// The offer price is shown in the seller's currency; when the list price is tax-inclusive and
// in the same currency, that tax-inclusive price is the starting point for the discount.
function listingStartingPrice(listing: RQLAutoOfferListingFragment.Fragment) {
  if (listingTaxIncluded(listing) && userCurrencyMatchesShopCurrency(listing)) {
    return listing.price;
  }

  return listing.sellerPrice;
}

function offerPriceDisplay(listing: RQLAutoOfferListingFragment.Fragment, percentOff: number): string | null {
  // Don't show a calculated price when the seller's display currency differs
  // from their shop currency — the discount math would mix currencies.
  if (!userCurrencyMatchesShopCurrency(listing)) return null;

  const startingPrice = listingStartingPrice(listing);
  if (!startingPrice?.amountCents || !percentOff) return null;

  const symbol = Currency.get(startingPrice.currency)?.symbol ?? '';
  return `${symbol}${discountedPrice(startingPrice.amountCents, startingPrice.currency, percentOff)}`;
}

function ListingSummaryCard({ listing }: { listing: RQLAutoOfferListingFragment.Fragment }) {
  const imageUrl = listing.images?.[0]?.source || BLANK_MEDIUM_IMAGE;
  const yourCostDisplay = listing.sellerCost?.display;

  return (
    <div className="direct-offers-modal__summary">
      <div className="direct-offers-modal__summary-image">
        <img src={imageUrl} alt={listing.title} />
      </div>
      <div className="direct-offers-modal__summary-grid">
        <RCText.Utility
          className="direct-offers-modal__summary-title"
          size="400"
          weight="semibold"
          maxLines={1}
        >
          {listing.title}
        </RCText.Utility>
        <RCText.Utility className="direct-offers-modal__summary-detail" size="400">
          <strong>{t('summary.price')}</strong> {listing.sellerPrice.display}
        </RCText.Utility>
        {yourCostDisplay ? (
          <RCText.Utility className="direct-offers-modal__summary-detail" size="400">
            <strong>{t('summary.yourCost')}</strong> {yourCostDisplay}
          </RCText.Utility>
        ) : (
          // Spacer keeps the desktop 2-col grid aligned; hidden on mweb where details stack.
          <span className="direct-offers-modal__summary-detail-placeholder" aria-hidden="true" />
        )}
        <RCText.Utility size="400">
          <RCIcon icon={<HeartStraightIcon />} size="inline" spacing="right" />
          {t('summary.saved', { count: listing.counts?.watchers ?? 0 })}
        </RCText.Utility>
        <RCText.Utility size="400">
          <RCIcon icon={<ShoppingCartIcon />} size="inline" spacing="right" />
          {t('summary.carts', { count: listing.counts?.inBuyerCarts ?? 0 })}
        </RCText.Utility>
      </div>
    </div>
  );
}

interface DirectOfferFormOptions {
  listing: RQLAutoOfferListingFragment.Fragment;
  loading: boolean;
  createAutoOffer: IApolloProps['createAutoOffer'];
  deleteAutoOffer: IApolloProps['deleteAutoOffer'];
  // Runs after a successful save so each surface can react on its own terms: the
  // modal closes and refetches, the standalone mobile page confirms in place.
  onSaved: (action: AutoOfferAction, appliedOfferPercent?: number) => void | Promise<void>;
}

export function useDirectOfferForm({
  listing,
  loading,
  createAutoOffer,
  deleteAutoOffer,
  onSaved,
}: DirectOfferFormOptions) {
  const [formState, dispatch] = React.useReducer(formReducer, emptyFormState);
  const [includeOfferMessage, setIncludeOfferMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!loading && hasAutoOffer(listing)) {
      dispatch({ type: 'prefillFormState', value: { listing } });
      setIncludeOfferMessage(!!listing.autoOffers.message);
    }
  }, [listing, loading]);

  const sellerPriceCurrencyCode = !loading && listing && listing.sellerPrice.currency;
  const isCarrierCalculatedListing = !loading && listing && !!listing.shipmentPackage;
  const percentageValidationMessage = formState.autoOfferIsActive
    ? percentageValidationError(formState)
    : null;

  const isDeleting = hasAutoOffer(listing) && !formState.autoOfferIsActive;
  const nothingToSave = !hasAutoOffer(listing) && !formState.autoOfferIsActive;
  const missingShippingOverride = !!isCarrierCalculatedListing && !formState.shippingPriceOverride?.amount;
  const formIsInvalid = formState.autoOfferIsActive
    && (!!percentageValidationMessage || missingShippingOverride);

  const saveDisabled = loading || submitting || nothingToSave || formIsInvalid;

  const offerPrice =
    formState.autoOfferIsActive && !percentageValidationMessage
      ? offerPriceDisplay(listing, formState.percentage)
      : null;
  // Only show the tax hint when the tax-inclusive list price is the discount base
  // (see listingStartingPrice); on a currency mismatch the base is sellerPrice, so the
  // hint would misrepresent the shown price.
  const offerPriceTaxHint =
    offerPrice && listingTaxIncluded(listing) && userCurrencyMatchesShopCurrency(listing)
      ? listing.taxIncludedHint
      : null;

  async function handleSave() {
    dispatch({ type: 'setSubmitFailedMessage', value: '' });
    setSubmitting(true);

    try {
      if (isDeleting) {
        await deleteAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
            },
          },
        });
      } else if (formState.autoOfferIsActive) {
        await createAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
              message: includeOfferMessage ? formState.messageToBuyer : '',
              percentage: formState.percentage,
              shippingPriceOverride: isCarrierCalculatedListing ? formState.shippingPriceOverride : null,
            },
          },
        });
      }

      await onSaved(
        isDeleting ? AUTO_OFFER_ACTION.DELETED : AUTO_OFFER_ACTION.CREATED,
        formState.percentage,
      );
    } catch (err) {
      dispatch({ type: 'setSubmitFailedMessage', value: t('submitFailed') });
    } finally {
      setSubmitting(false);
    }
  }

  return {
    formState,
    dispatch,
    includeOfferMessage,
    setIncludeOfferMessage,
    submitting,
    sellerPriceCurrencyCode,
    isCarrierCalculatedListing,
    percentageValidationMessage,
    saveDisabled,
    offerPrice,
    offerPriceTaxHint,
    handleSave,
  };
}

export type DirectOfferForm = ReturnType<typeof useDirectOfferForm>;

interface DirectOfferFormBodyProps {
  form: DirectOfferForm;
  listing: RQLAutoOfferListingFragment.Fragment;
  loading: boolean;
}

export function DirectOfferFormBody({ form, listing, loading }: DirectOfferFormBodyProps) {
  const {
    formState,
    dispatch,
    includeOfferMessage,
    setIncludeOfferMessage,
    sellerPriceCurrencyCode,
    isCarrierCalculatedListing,
    percentageValidationMessage,
    offerPrice,
    offerPriceTaxHint,
  } = form;
  const learnMoreUrl = useHelpCenterArticleUrl(DIRECT_OFFER_HELP_CENTER_ARTICLE_ID);
  // Open the Help Center article in a new tab on desktop so sellers don't lose the form.
  const isDesktop = useMediaQuery('desktop') ?? true;

  return (
    <>
      {loading || !listing ? (
        <div className="d-flex fx-justify-center">
          <RCLoadingBars />
        </div>
      ) : (
        <div className="direct-offers-modal__content">
          <ListingSummaryCard listing={listing} />
          <RCText.Default>
            {t('description')}
            {' '}
            <CoreLink
              className="td-underline fg-color-text-primary"
              to={learnMoreUrl}
              target={isDesktop ? '_blank' : undefined}
              rel={isDesktop ? 'noopener noreferrer' : undefined}
            >
              {t('learnMore')}
            </CoreLink>
          </RCText.Default>

          <RCCheckbox
            id="toggleAutoOffers"
            name="toggleAutoOffers"
            checked={formState.autoOfferIsActive}
            label={t('enable')}
            onChange={() => dispatch({ type: 'toggleOfferActive' })}
          />

          {formState.autoOfferIsActive && (
            <div className="direct-offers-modal__form">
              <div className="direct-offers-modal__percent-row">
                <div className="direct-offers-modal__percent-input">
                  <RCTextInput
                    id="percentage"
                    name="percentage"
                    label={t('percentOff.label')}
                    value={formState.percentage ? formState.percentage.toString() : ''}
                    onChange={(evt) => dispatch({ type: 'setPercentage', value: evt.target.value })}
                    suffixText="%"
                    required
                    errorText={percentageValidationMessage}
                  />
                </div>
                <RCText.Default className="direct-offers-modal__offer-price">
                  <strong>{t('percentOff.directOfferPrice')}</strong>
                  {' '}
                  {offerPrice}
                  {offerPriceTaxHint && ` ${t('percentOff.taxIncludedHint', { hint: offerPriceTaxHint })}`}
                </RCText.Default>
              </div>

              {isCarrierCalculatedListing && (
                <div className="direct-offers-modal__shipping">
                  <RCText.Default>
                    <strong>{t('carrierCalculated.header')}</strong>
                    {' '}
                    {t('carrierCalculated.description')}
                  </RCText.Default>
                  <MoneyInput
                    id="directOfferShipping"
                    name="directOfferShipping"
                    currencyCode={sellerPriceCurrencyCode}
                    label={t('carrierCalculated.inputLabel')}
                    value={formState.shippingPriceOverride?.amount || ''}
                    onChange={(evt) => dispatch({ type: 'setShippingPriceOverride', value: evt.target.value })}
                    required
                  />
                </div>
              )}

              <RCCheckbox
                id="includeOfferMessage"
                name="includeOfferMessage"
                checked={includeOfferMessage}
                label={t('includeMessage')}
                onChange={() => setIncludeOfferMessage(!includeOfferMessage)}
              />

              {includeOfferMessage && (
                <RCTextArea
                  id="messageToBuyer"
                  name="messageToBuyer"
                  label={t('message.label')}
                  tag="optional"
                  value={formState.messageToBuyer}
                  onChange={(evt) => dispatch({ type: 'setMessageToBuyer', value: evt.target.value })}
                />
              )}
            </div>
          )}

          {formState.submitFailedMessage && (
            <RCText.Default color="urgent">
              {formState.submitFailedMessage}
            </RCText.Default>
          )}
        </div>
      )}
    </>
  );
}

export function AutoOfferModal(props: Props) {
  const { loading, listing } = props.data;

  const form = useDirectOfferForm({
    listing,
    loading,
    createAutoOffer: props.createAutoOffer,
    deleteAutoOffer: props.deleteAutoOffer,
    onSaved: (action, appliedOfferPercent) => {
      props.onModalClose();

      if (props.refetchSellerListing) {
        props.refetchSellerListing(action, appliedOfferPercent);
      } else {
        Location.reload();
      }
    },
  });

  return (
    <RCModal
      isOpen={props.modalIsOpen}
      title={t('title')}
      onOpenChange={props.onModalClose}
      actions={{
        primary: {
          onClick: form.handleSave,
          buttonText: t('save'),
          preventCloseOnClick: true,
          disabled: form.saveDisabled,
        },
        secondary: true,
      }}
    >
      <DirectOfferFormBody form={form} listing={listing} loading={loading} />
    </RCModal>
  );
}

export const AUTO_OFFER_LISTING_QUERY = gql(`
    query Core_AutoOfferModal($listingId: String) {
      listing(input: { id: $listingId }) {
        _id
        ...AutoOfferListingFragment
      }
    }
  `);

const connected = withGraphql<ExternalProps>(
  AUTO_OFFER_LISTING_QUERY,
  {
    options: {
      ssr: false, // not important for SEO or initial page rendering
      fetchPolicy: 'network-only',
    },
  },
);

export default connect<ExternalProps>([
  createAutoOfferMutation,
  deleteAutoOfferMutation,
  connected,
])(AutoOfferModal);
